import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import JourneyTimeline from "../components/journey_timeline";
import type { JourneyTimelineRef } from "../components/journey_timeline";
import WorldMap from "../components/locations";

const BASE_URL = process.env.PUBLIC_URL || "";

const managementTeam = [
  {
    id: "asker-laubjerg",
    href: "https://www.linkedin.com/in/asker-laubjerg-b7203834/",
    src: "/assets/asker.png",
    hover: "/assets/2025-3.png",
    alt: "Asker Laubjerg",
  },
  {
    id: "mandy-ma",
    href: "https://www.linkedin.com/in/mandy-ma-a44b2096/",
    src: "/assets/mandy.png",
    hover: "/assets/2025-4.png",
    alt: "Mandy Ma",
  },
  {
    id: "mridul-dasgupta",
    href: "https://www.linkedin.com/in/mridul-dasgupta-5187b99/",
    src: "/assets/mridul.png",
    hover: "/assets/2025-1.png",
    alt: "Mridul Dasgupta",
  },
  {
    id: "deepak-menon",
    href: "https://www.linkedin.com/in/deepak-menon-84343022/",
    src: "/assets/deepak.png",
    hover: "/assets/2025-7.png",
    alt: "Deepak Menon",
  },
  {
    id: "tommy-loy",
    href: "https://www.linkedin.com/in/tommy-loy-26354712/",
    src: "/assets/tommy.png",
    hover: "/assets/2025-11.png",
    alt: "Tommy Loy",
  },
  {
    id: "team-2025-12",
    src: "/assets/albert.png",
    hover: "/assets/2025-13.png",
    alt: "Management Team Member",
  },
  {
    id: "team-2025-17",
    href: "https://www.linkedin.com/in/pervez-alam/",
    src: "/assets/pervez.png",
    hover: "/assets/2025-18.png",
    alt: "Management Team Member",
  },
  {
    id: "team-2025-17",
    href: "https://www.linkedin.com/in/matt-bowen-99698556/",
    src: "/assets/matt1.png",
    hover: "/assets/mattbowen.png",
    alt: "Management Team Member",
  },
];

interface ValueBoxProps {
  title: string;
  description: string;
  defaultImage: string;
  defaultBgColor: string;
  hoverBgColor?: string;
  hoverImage: string;
  sx?: any;
}

function ValueBox({
  title,
  description,
  defaultImage,
  hoverImage,
  defaultBgColor,
  sx,
}: ValueBoxProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        bgcolor: hovered ? "common.white" : defaultBgColor,
        border: `4px solid ${defaultBgColor}`,
        borderRadius: "24px 24px 0px 24px",
        minHeight: { xs: 290, md: 370 },
        p: { xs: "36px 20px", md: "36px 28px" },
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        ...sx,
      }}
    >
      <Box sx={{ zIndex: 2 }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "1.9rem", md: "clamp(2rem, 2.8vw, 3rem)" },
            fontWeight: 800,
            color: hovered ? defaultBgColor : "common.white",
            transition: "color 0.3s ease",
            lineHeight: 1.1,
            mb: 2,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "1.2rem", md: "clamp(1rem, 1.2vw, 1.35rem)" },
            color: defaultBgColor,
            lineHeight: 1.4,
            fontWeight: 500,
            opacity: hovered ? 1 : 0,
            transform: hovered ? "translateY(0)" : "translateY(15px)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
            maxWidth: "95%",
          }}
        >
          {description}
        </Typography>
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: { xs: 16, md: 30 },
          right: { xs: 16, md: 30 },
          width: { xs: 80, md: 100 },
          height: { xs: 80, md: 100 },
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          bgcolor: hovered ? defaultBgColor : "common.white",
          boxShadow: hovered ? "none" : "0 4px 10px rgba(0,0,0,0.1)",
          transition: "all 0.4s ease",
          transform: hovered ? "scale(1.1)" : "scale(1)",
        }}
      >
        <Box sx={{ position: "relative", width: "70%", height: "70%" }}>
          <Box
            component="img"
            src={defaultImage}
            alt={title}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: hovered ? 0 : 1,
              transition: "opacity 0.3s ease",
            }}
          />

          <Box
            component="img"
            src={hoverImage}
            alt={`${title} hovered`}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: hovered ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

const SECTION_PADDING = {
  xs: "40px 24px",
  sm: "48px 32px",
  md: "64px 64px",
  lg: "80px 80px",
};

const sectionHeadingSx = {
  mb: 4,
  textAlign: "left",
};

function AboutUs() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const visibleTeam = managementTeam.slice(0, 8);
  const timelineRef = useRef<JourneyTimelineRef>(null);

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "60vh", md: "75vh" },
          overflow: "hidden",
        }}
      >
        <Box
          component="video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        >
          <source
            src={`${BASE_URL}/assets/aboutusvideo.mp4`}
            type="video/mp4"
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: "24px", md: "64px" },
            transform: "translateY(-50%)",
            color: "common.white",
            maxWidth: { xs: "calc(100% - 48px)", md: 700 },
            zIndex: 2,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: { xs: "2.5rem", md: "clamp(2.2rem, 3.5vw, 3.5rem)" },
              animation: "heroFadeUp 0.9s ease both",
              "@keyframes heroFadeUp": {
                from: { opacity: 0, transform: "translateY(36px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Driving Value and Quality
            <br />
            Through Sustainable
            <br /> Sourcing
          </Typography>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 8 },
          // Keeping left padding minimal to allow image to sit closer to the edge
          pl: { xs: 3, md: "40px" },
          pr: { xs: 3, md: "40px" }, // Balanced right padding
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            maxWidth: 1400,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              flex: { xs: "1", md: "0 0 40%" },
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={`${BASE_URL}/assets/au-image1-v2.png`}
              alt="About Us"
              sx={{
                width: "100%",
                borderRadius: "40px", // Softer corners like your sample image
                display: "block",
              }}
            />
          </Box>

          {/* TEXT BOX: Pushed to the right */}
          <Box
            sx={{
              flex: 1,
              // The "Move Right" secret: A large left margin on desktop
              ml: { md: "150px", lg: "120px" },
              mt: { xs: 4, md: 2 }, // Space for mobile stack
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem", lg: "1.2rem" },
                lineHeight: 1.4,
                letterSpacing: "0.01em",
                mb: 3,
                color: "text.primary",
              }}
            >
              Anko Sourcing is the exclusive direct sourcing arm of Kmart Group
              that operates the iconic retail brands Kmart and Target Australia.
              Anko Sourcing's operations span the largest sourcing markets
              across Asia, including China, India, Bangladesh, Cambodia,
              Indonesia and Vietnam.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem", lg: "1.2rem" },
                lineHeight: 1.4,
                letterSpacing: "0.01em",
                color: "text.primary",
              }}
            >
              The focus of Anko Sourcing is on building strong, sustainable
              supplier partnerships to ensure we maintain the highest standards
              of quality and ethics while driving efficiency and innovation
              across our supply chains.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          pt: { xs: "20px", sm: "28px", md: "40px", lg: "48px" },
          pb: SECTION_PADDING.xs.split(" ")[0],
          px: SECTION_PADDING.xs.split(" ")[1],
          "@media (min-width:600px)": {
            pb: SECTION_PADDING.sm.split(" ")[0],
            px: SECTION_PADDING.sm.split(" ")[1],
          },
          "@media (min-width:900px)": {
            pb: SECTION_PADDING.md.split(" ")[0],
            px: SECTION_PADDING.md.split(" ")[1],
          },
          "@media (min-width:1200px)": {
            pb: SECTION_PADDING.lg.split(" ")[0],
            px: SECTION_PADDING.lg.split(" ")[1],
          },
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h2" sx={{ textAlign: "left", m: 0 }}>
            Our Journey
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <IconButton
              onClick={() => timelineRef.current?.scrollPrev()}
              sx={{
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 },
                borderWidth: "2px",
                borderStyle: "solid",
                borderColor: "#b7127e",
                borderRadius: "50%",
                bgcolor: "transparent",
                color: "#000",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "#b7127e",
                  borderColor: "#b7127e",
                  color: "#fff",
                },
              }}
            >
              &#8249;
            </IconButton>
            <IconButton
              onClick={() => timelineRef.current?.scrollNext()}
              sx={{
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 },
                borderWidth: "2px",
                borderStyle: "solid",
                borderColor: "#b7127e",
                borderRadius: "50%",
                bgcolor: "transparent",
                color: "#000",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "#b7127e",
                  borderColor: "#b7127e",
                  color: "#fff",
                },
              }}
            >
              &#8250;
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 0, md: 0 },
          px: { xs: 0, md: 0 },
        }}
      >
        <JourneyTimeline ref={timelineRef} />
      </Box>

      <Box
        component="section"
        sx={{
          p: SECTION_PADDING,
          bgcolor: "background.default",
        }}
      >
        <Typography variant="h2" sx={sectionHeadingSx}>
          Management Team
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
            },
            columnGap: { xs: 3, md: "65px" },
            rowGap: { xs: 3, md: 7 },
            mt: 3,
          }}
        >
          {visibleTeam.map((member) => (
            <Box key={member.id}>
              <Box
                component={member.href ? "a" : "div"}
                href={member.href || undefined}
                target={member.href ? "_blank" : undefined}
                rel={member.href ? "noreferrer" : undefined}
                sx={{
                  display: "block",
                  position: "relative",
                  textDecoration: "none",
                  lineHeight: 0,
                  cursor: member.href ? "pointer" : "default",
                  "& .hover-img": {
                    display: "none",
                  },
                  "&:hover .hover-img": {
                    display: "block",
                  },
                  "&:hover .base-img": {
                    display: "none",
                  },
                }}
              >
                <Box
                  component="img"
                  className="base-img"
                  src={`${BASE_URL}${member.src}`}
                  alt={member.alt}
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: 0,
                  }}
                />

                {member.hover && (
                  <Box
                    component="img"
                    className="hover-img"
                    src={`${BASE_URL}${member.hover}`}
                    alt={`${member.alt} info`}
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      borderRadius: 0,
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          pt: SECTION_PADDING.xs.split(" ")[0],
          pb: { xs: 2, md: 3 },
          px: SECTION_PADDING.xs.split(" ")[1],
          "@media (min-width: 600px)": {
            pt: SECTION_PADDING.sm.split(" ")[0],
            px: SECTION_PADDING.sm.split(" ")[1],
          },
          "@media (min-width: 960px)": {
            pt: SECTION_PADDING.md.split(" ")[0],
            px: SECTION_PADDING.md.split(" ")[1],
          },
          "@media (min-width: 1280px)": {
            pt: SECTION_PADDING.lg.split(" ")[0],
            px: SECTION_PADDING.lg.split(" ")[1],
          },
          bgcolor: "background.default",
        }}
      >
        <Typography variant="h2" sx={sectionHeadingSx}>
          Our Commitment
        </Typography>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 2, md: 3 },
          // Keeping left padding minimal to allow image to sit closer to the edge
          pl: { xs: 3, md: "40px" },
          pr: { xs: 3, md: "40px" }, // Balanced right padding
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            maxWidth: 1400,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              flex: { xs: "1", md: "0 0 40%" },
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={`${BASE_URL}/assets/au-2-v2.png`}
              alt="Better Together"
              sx={{
                width: "100%",
                borderRadius: "40px", // Softer corners like your sample image
                display: "block",
              }}
            />
          </Box>

          {/* TEXT BOX: Pushed to the right */}
          <Box
            sx={{
              flex: 1,
              // The "Move Right" secret: A large left margin on desktop
              ml: { md: "150px", lg: "120px" },
              mt: { xs: 4, md: 2 }, // Space for mobile stack
            }}
          >
            <Typography
              variant="h3"
              sx={{
                mb: 1.5,
                fontSize: { xs: "2rem", md: "3.125rem", lg: "3.5rem" },
                letterSpacing: 0.1,
                color: "#040404",
              }}
            >
              Better Together
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.5,
                mb: 2.5,
                fontSize: { xs: "1rem", md: "1.125rem", lg: "1.2rem" },
                fontWeight: 500,
              }}
            >
              "Better Together" is Kmart Group's integrated sustainability plan
              and core strategy, fostering collaboration to create a more
              sustainable future with our partners, customers, and communities.
            </Typography>
            <Button
              variant="outlined"
              component="a"
              href="https://www.kmart.com.au/bettertogether/"
              target="_blank"
              rel="noreferrer"
              sx={{
                borderRadius: "24px 24px 0px 24px",
                borderColor: "#b7127e",
                color: "#b7127e",
                px: 4,
                py: 1.2,
                fontWeight: 500,
                textTransform: "none",
                borderWidth: "1.5px",
                "&:hover": {
                  bgcolor: "#b7127e",
                  color: "common.white",
                  borderColor: "#b7127e",
                  borderWidth: "1.5px",
                },
              }}
            >
              Know More
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          p: SECTION_PADDING,
          bgcolor: "background.default",
        }}
      >
        <Typography variant="h2" sx={sectionHeadingSx}>
          Our Locations
        </Typography>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          <WorldMap />
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: "40px", sm: "48px", md: "64px", lg: "80px" },
          px: { xs: "24px", sm: "32px", md: "64px", lg: "80px" },
        }}
      >
        <Box sx={{ maxWidth: 1600, mx: "auto", width: "100%" }}>
          {/* TOP ROW: Uses Grid for 3 equal columns */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 7,
              mb: 7, // Space between top and bottom rows
            }}
          >
            {[
              {
                title: "Think Customer",
                desc: "We put the customer at the heart of everything we do.",
                img: "Bin1.png",
                hover: "hoverimg1.png",
                color: "#b7127e",
              },
              {
                title: "Take Care",
                desc: "We look after ourselves, our teams, customers, contractors and our community.",
                img: "Bin-2.png",
                hover: "hoverimg2.png",
                color: "#64a70b",
              },
              {
                title: "Live Integrity",
                desc: "We always do the right thing.",
                img: "Bin-3.png",
                hover: "hoverimg3.png",
                color: "#d78a1e",
              },
            ].map((v) => (
              <ValueBox
                key={v.title}
                title={v.title}
                description={v.desc}
                defaultImage={`${BASE_URL}/assets/${v.img}`}
                hoverImage={`${BASE_URL}/assets/${v.hover}`}
                defaultBgColor={v.color}
                sx={{
                  aspectRatio: { md: "3 / 3.6" },
                  minHeight: { xs: 290, md: 350 },
                }}
              />
            ))}
          </Box>

          {/* BOTTOM ROW: Uses Flex to center the 2 remaining cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center", // This centers the two cards horizontally
              gap: 7,
              flexWrap: "wrap",
            }}
          >
            {[
              {
                title: "Grow Together",
                desc: "We achieve the best results working together.",
                img: "Bin-4.png",
                hover: "hoverimg4.png",
                color: "#684e96",
              },
              {
                title: "Reach Higher",
                desc: "We are bold and relentless in delivering improvements both big and small.",
                img: "Bin-5.png",
                hover: "hoverimg5.png",
                color: "#0f9aae",
              },
            ].map((v) => (
              <Box
                key={v.title}
                sx={{
                  width: {
                    xs: "100%",
                    // Match the 2-column grid math for tablet (100% minus 1 gap, divided by 2)
                    sm: "calc((100% - 56px) / 2)",
                    // Match the 3-column grid math for desktop (100% minus 2 gaps, divided by 3)
                    md: "calc((100% - 112px) / 3)",
                  },
                  flex: "0 0 auto", // Prevents flex stretching
                }}
              >
                <ValueBox
                  title={v.title}
                  description={v.desc}
                  defaultImage={`${BASE_URL}/assets/${v.img}`}
                  hoverImage={`${BASE_URL}/assets/${v.hover}`}
                  defaultBgColor={v.color}
                  sx={{
                    aspectRatio: { md: "3 / 3.6" },
                    minHeight: { xs: 290, md: 350 },
                    width: "100%",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AboutUs;
